import {
  IframePidgetContext,
  IframePidgetSettings
} from "@workspace/utils-react";
import { useContext, useMemo } from "react";
import { WorkspaceContext } from "../utils/navigation/openCollinsAdminLink";

/**
 * Returns the Workspace context (workspaceUrl and instanceId) when
 * running inside an Evo native tab, or undefined otherwise.
 *
 * Instead of calling useIframePidgetSettings / useIframePidgetInstance
 * (which throw when no IframePidgetProvider is present), we read the
 * underlying IframePidgetContext directly. The context defaults to null
 * when the provider is absent, so we safely return undefined and let
 * downstream utilities fall back to standard browser navigation.
 *
 * This avoids any Rules-of-Hooks concerns because useContext never
 * throws — it simply returns the default value.
 */
const useWorkspaceContext = (
  isInIframe: boolean
): WorkspaceContext | undefined => {
  const pidgetContext = useContext(IframePidgetContext);

  return useMemo(() => {
    if (!isInIframe || !pidgetContext) {
      return undefined;
    }

    const settings = pidgetContext.iframePidget.settings as
      | IframePidgetSettings
      | undefined;
    const instanceId = pidgetContext.iframePidget.instanceId;

    if (!settings?.workspaceUrl || !instanceId) {
      return undefined;
    }

    return {
      workspaceUrl: settings.workspaceUrl,
      instanceId
    };
  }, [isInIframe, pidgetContext]);
};

export default useWorkspaceContext;
